import type { Primitive } from "ts-essentials";

import { readState } from "@/__main__/app-state.mjs";
import { IS_DEV } from "@/__main__/constants.mjs";
import router, { ROUTE_INITIATED_BY_AUTO } from "@/__main__/router.mjs";
import { GAME_SYMBOL_BY_SHORT_NAME } from "@/app/constants.mjs";
import type { AppErrorEvent } from "@/app/ErrorBoundary.jsx";
import ANALYTIC_EVENTS from "@/feature-analytics/analytic-events.mjs";
import { GA4_WEB_DOWNLOAD_EVENT } from "@/feature-analytics/google.mjs";
import { refs__PRIVATE as refs } from "@/feature-analytics/tracker.mjs";
import { getUTMInfo, getUTMPromise } from "@/feature-analytics/utm.mjs";
import { GAME_SYMBOL_UNKNOWN } from "@/game-unknown/definition-symbol.mjs";
import type { OverlayVisibility } from "@/hub-overlays/handle-messages.mjs";
import { getEnabledNativeOverlays } from "@/hub-overlays/utils.mjs";
import { devDebug, devWarn } from "@/util/dev.mjs";
import globals from "@/util/global-whitelist.mjs";
import isRouteOverlay from "@/util/is-route-overlay.mjs";
import { stringifyPath } from "@/util/path-regexp.mjs";

export function onAppStart() {
  return refs.trackEvent(ANALYTIC_EVENTS.SESSION, {});
}

export async function onAppDownload(data) {
  // data = { origin, url }
  await getUTMPromise();

  refs.gtag("event", GA4_WEB_DOWNLOAD_EVENT, await refs.getIdentifyingInfo());

  return refs.trackEvent(
    ANALYTIC_EVENTS.APP_DOWNLOAD,
    Object.assign(
      {
        [refs.isImmediate]: true,
      },
      getUTMInfo(),
      data,
    ),
  );
}

export function onAppError(data: AppErrorEvent) {
  const mappedData: Record<string, Primitive | object | unknown> = {
    ...data,
  };

  const { error } = data;

  mappedData.error =
    error instanceof Error ? error.toString() : JSON.stringify(error);

  if (error instanceof Error && Object.keys(error).length) {
    mappedData.errorData = { ...error };
  }

  return refs.trackEvent(ANALYTIC_EVENTS.APP_CLIENT_ERROR, mappedData);
}

let timeStamp;
export async function onRouteChange(route) {
  if (route.currentPath === router.previousRoute?.currentPath) return null;
  await getUTMPromise();

  const pageDetails = {
    route: stringifyPath(route.path),
    url: globals.location.href,
    referrer: globals.document?.referrer,
    routeParams: route.parameters,
    prevRoute: router.previousRoute?.currentPath,
    timeSpentOnPrevRoute: router.previousRoute?.currentPath
      ? Date.now() - timeStamp
      : 0,
    isAutomaticRedirect: route.initiatedBy === ROUTE_INITIATED_BY_AUTO,
    utm: getUTMInfo(),
  };

  //Set timeStamp after object has been created so we can track
  //the currentRoute's time. Then the next time onRouteChange
  //is called we can calculate how long a user stayed on that route.
  timeStamp = Date.now();

  return refs.trackEvent(ANALYTIC_EVENTS.PAGE_VIEW, pageDetails);
}

export function onNetworkError(url) {
  // TODO: try to normalize URLs
  return refs.trackEvent(ANALYTIC_EVENTS.NETWORK_ERROR, {
    url,
  });
}

export function onOverlayVisibility(data: OverlayVisibility) {
  // TEMP: disabled to prevent spamming
  if (!IS_DEV) return;
  const gameSymbol =
    GAME_SYMBOL_BY_SHORT_NAME[data.game] || GAME_SYMBOL_UNKNOWN;
  const { currentGameTuple = [] } = readState.volatile;
  const [currentGame, gameId] = currentGameTuple;

  // ensure game matches most recent current game record
  if (currentGame !== gameSymbol)
    return devWarn("onOverlayVisibility: mismatched game", {
      fromCore: data.game,
      fromApp: currentGame,
    });

  // log from overlay instance only, if available
  if (data.hasWebOverlay && !isRouteOverlay())
    return devDebug(
      "skipping OVERLAY_VISIBILITY event in favor of electron instance",
    );

  // order by name
  const overlays = data.visible
    ? [
        ...(data.webOverlays || []),
        ...getEnabledNativeOverlays(gameSymbol),
      ].sort((a, b) => a.localeCompare(b))
    : [];

  Object.assign(data, { overlays, gameId });

  return refs.trackEvent(ANALYTIC_EVENTS.OVERLAY_VISIBILITY, data);
}

export function onUninstallSurvey(payload) {
  return refs.trackEvent(
    ANALYTIC_EVENTS.UNINSTALL_SURVEY,
    Object.assign(
      {
        [refs.isImmediate]: true,
      },
      payload,
    ),
  );
}

export function onPromoModalClose(payload) {
  return refs.trackEvent(ANALYTIC_EVENTS.PROMO_MODAL_CLOSE, payload);
}

export function onInteraction(payload) {
  return refs.trackEvent(ANALYTIC_EVENTS.USER_INTERACTION, payload);
}

export function onRefreshApp(payload) {
  return refs.trackEvent(
    ANALYTIC_EVENTS.HIGH_MEMORY_REFRESH,
    Object.assign(
      {
        [refs.isImmediate]: true,
      },
      payload,
    ),
  );
}

export function onTimedRefreshApp(payload) {
  return refs.trackEvent(
    ANALYTIC_EVENTS.TIMED_REFRESH,
    Object.assign(
      {
        [refs.isImmediate]: true,
      },
      payload,
    ),
  );
}

// There are some issues with the way the data team obtains feature versions. We temporarily added this event to report it and will remove it after they fix it.
export function onFeatureVersionChange(payload) {
  return refs.trackEvent(ANALYTIC_EVENTS.FEATURE_VERSION, payload);
}
